import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatErr } from "../../../../../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../../components/ui/table";
import { toast } from "sonner";
import { SectionCard, EmptyState } from "../shared";
import SearchableSelect from "../SearchableSelect";
import { ACCOUNTING_BASE } from "../accountingNav";
import { ReportFilterBar, ReportPrintShell, useFinancialYears, downloadReportCsv, formatCurrency, formatBalance, formatDateDMY } from "./reportShared";

/**
 * Phase 8 - Cash Book (items 16-19, 47).
 *
 * Behaves like Bank Book but against a Cash-group ledger - if the Society
 * has exactly one, it's auto-selected; more than one requires an explicit
 * pick (never a merged/generic figure). Money received increases Cash
 * (Cash In), money paid reduces it (Cash Out); a negative closing balance
 * is flagged, never silently blocked (item 18).
 */
export default function CashBookPage() {
  const navigate = useNavigate();
  const financialYears = useFinancialYears();
  const [cashLedgers, setCashLedgers] = useState([]);
  const [ledgerId, setLedgerId] = useState("");
  const [financialYearId, setFinancialYearId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/ledgers");
        const rows = (Array.isArray(res.data) ? res.data : []).filter((l) => l.ledgerGroup === "cash");
        setCashLedgers(rows);
        if (rows.length === 1) setLedgerId(rows[0].id);
      } catch { setCashLedgers([]); }
    })();
  }, []);
  const ledgerOptions = useMemo(() => cashLedgers.map((l) => ({ value: l.id, label: l.name })), [cashLedgers]);

  const params = { ledgerId, financialYearId, fromDate, toDate };

  const load = async () => {
    if (cashLedgers.length > 1 && !ledgerId) { setData(null); setLoading(false); return; }
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/society/accounting/reports/cash-book", { params });
      setData(res.data);
    } catch (err) {
      setError(formatErr(err, "Could not load cash book"));
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [ledgerId, financialYearId, fromDate, toDate, cashLedgers.length]);

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-extrabold text-[#111827]">Cash Book</h2>
        <p className="mt-1 text-sm text-[#6B7280]">Cash In / Cash Out and running Cash balance.</p>
      </div>

      <div className="report-screen-only space-y-5">
        <ReportFilterBar
          financialYears={financialYears} financialYearId={financialYearId} onFinancialYearChange={setFinancialYearId}
          fromDate={fromDate} toDate={toDate} onFromDateChange={setFromDate} onToDateChange={setToDate}
          extra={cashLedgers.length > 1 ? (
            <div className="w-64 space-y-1">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">Cash Ledger</div>
              <SearchableSelect value={ledgerId} onChange={setLedgerId} options={ledgerOptions} placeholder="Select cash ledger" />
            </div>
          ) : undefined}
          onExport={data ? () => downloadReportCsv("/society/accounting/reports/cash-book", params, "cash-book.csv") : undefined}
          onPrint={data ? () => window.print() : undefined}
        />

        {cashLedgers.length > 1 && !ledgerId ? (
          <EmptyState>This Society has more than one Cash ledger - select one above.</EmptyState>
        ) : loading ? (
          <div className="text-sm text-[#6B7280]">Loading...</div>
        ) : error ? (
          <EmptyState>{error}</EmptyState>
        ) : !data ? (
          <EmptyState>No Cash ledger set up yet.</EmptyState>
        ) : (
          <>
            {data.negative && (
              <div className="rounded-sm border border-red-300 bg-red-50 p-3 text-sm font-semibold text-red-700">
                Closing Cash balance is negative - this usually indicates a data or migration issue, please review.
              </div>
            )}
            <SectionCard title={data.ledgerName} subtitle={`${data.financialYear ? `FY ${data.financialYear} — ` : ""}${data.fromDate} to ${data.toDate}`}>
              <div className="mb-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Opening Cash</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(Math.abs(data.openingBalance))}</div></div>
                <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Total Cash In</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodDebit)}</div></div>
                <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Total Cash Out</div><div className="mt-0.5 text-sm font-bold">{formatCurrency(data.periodCredit)}</div></div>
                <div><div className="text-xs uppercase tracking-[0.14em] text-[#6B7280]">Closing Cash</div><div className={`mt-0.5 text-sm font-bold ${data.negative ? "text-red-600" : ""}`}>{formatBalance(data.closingBalance, data.closingSide)}</div></div>
              </div>
              <div className="overflow-x-auto rounded-sm border border-[#E5E7EB]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead><TableHead>Voucher No.</TableHead><TableHead>Type</TableHead>
                      <TableHead>Particulars</TableHead><TableHead>Party</TableHead><TableHead>Reference</TableHead>
                      <TableHead className="text-right">Cash In</TableHead><TableHead className="text-right">Cash Out</TableHead><TableHead className="text-right">Running Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow className="bg-[#F9FAFB]">
                      <TableCell>Opening</TableCell><TableCell colSpan={2}>Opening Balance</TableCell><TableCell colSpan={3}>—</TableCell>
                      <TableCell className="text-right">—</TableCell><TableCell className="text-right">—</TableCell>
                      <TableCell className="text-right font-semibold">{formatCurrency(Math.abs(data.openingBalance))}</TableCell>
                    </TableRow>
                    {data.rows.map((row, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDateDMY(row.voucherDate)}</TableCell>
                        <TableCell>{row.voucherId ? <button type="button" className="font-semibold text-[#111827] hover:underline" onClick={() => navigate(`${ACCOUNTING_BASE}/vouchers/${row.voucherId}`)}>{row.voucherNumber || "(draft)"}</button> : "-"}</TableCell>
                        <TableCell>{row.voucherType}</TableCell>
                        <TableCell>{row.narration}</TableCell>
                        <TableCell>{row.partyName || "-"}</TableCell>
                        <TableCell>{row.referenceNo || "-"}</TableCell>
                        <TableCell className="text-right">{row.cashIn ? formatCurrency(row.cashIn) : "—"}</TableCell>
                        <TableCell className="text-right">{row.cashOut ? formatCurrency(row.cashOut) : "—"}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(Math.abs(row.runningBalance))}</TableCell>
                      </TableRow>
                    ))}
                    {data.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-sm text-[#6B7280]">No transactions in this range.</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </div>
            </SectionCard>
          </>
        )}
      </div>

      {data && (
        <div className="report-print-only">
          <ReportPrintShell reportName={`Cash Book — ${data.ledgerName}`} rangeLabel={`${data.fromDate} to ${data.toDate}`}>
            <table className="w-full border-collapse text-[10px]">
              <thead><tr className="border-b border-black"><th className="py-1 text-left">Date</th><th className="py-1 text-left">Voucher</th><th className="py-1 text-left">Particulars</th><th className="py-1 text-right">Cash In</th><th className="py-1 text-right">Cash Out</th><th className="py-1 text-right">Balance</th></tr></thead>
              <tbody>
                <tr className="border-b border-gray-300"><td className="py-0.5">Opening</td><td /><td className="py-0.5">Opening Balance</td><td /><td /><td className="py-0.5 text-right">{formatCurrency(Math.abs(data.openingBalance))}</td></tr>
                {data.rows.map((row, i) => (
                  <tr key={i} className="border-b border-gray-300">
                    <td className="py-0.5">{formatDateDMY(row.voucherDate)}</td>
                    <td className="py-0.5">{row.voucherNumber}</td>
                    <td className="py-0.5">{row.narration}</td>
                    <td className="py-0.5 text-right">{row.cashIn ? formatCurrency(row.cashIn) : ""}</td>
                    <td className="py-0.5 text-right">{row.cashOut ? formatCurrency(row.cashOut) : ""}</td>
                    <td className="py-0.5 text-right">{formatCurrency(Math.abs(row.runningBalance))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ReportPrintShell>
        </div>
      )}
    </div>
  );
}
